/**
 * Pencil Fonts Helpers
 * Handles font detection and validation for imported graphics
 */

import type { AnySceneObjectT, SimpleStyleT } from "tweenly"
import { SYSTEM_FONTS } from "../googleFonts.helpers"

/**
 * Check if a style is a fontFamily style
 */
const isFontFamilyStyle = (s: SimpleStyleT): boolean => s.property === "fontFamily"

/**
 * Collect non-system font names referenced in objects' fontFamily styles.
 */
export const collectReferencedFonts = (objects: AnySceneObjectT[]): Set<string> => {
    const fonts = new Set<string>()
    for (const obj of objects) {
        const ff = obj.styles.find(isFontFamilyStyle)
        if (ff && typeof ff.value === "string" && !SYSTEM_FONTS.has(ff.value)) {
            fonts.add(ff.value)
        }
    }
    return fonts
}

/**
 * Check which referenced fonts are present in the ZIP's assets/fonts/ folder.
 * Font files are named like "Roboto-400-latin.woff2" — the family name is the
 * first segment before the weight number.
 */
export const findMissingFonts = (
    referencedFonts: Set<string>,
    fontBlobs: Map<string, Blob>
): string[] => {
    if (referencedFonts.size === 0) return []

    // Build a set of font family names present in the ZIP
    const availableFamilies = new Set<string>()
    for (const path of Array.from(fontBlobs.keys())) {
        // path like "assets/fonts/Roboto-400-latin.woff2"
        const filename = path.split("/").pop() || ""
        // Extract family name: everything before the first "-<digit>" pattern
        const familyMatch = filename.match(/^(.+?)-\d/)
        if (familyMatch) {
            // Convert hyphens back to spaces for comparison
            availableFamilies.add(familyMatch[1].replace(/-/g, " "))
        }
    }

    return Array.from(referencedFonts).filter((font) => !availableFamilies.has(font))
}

/**
 * Parse font family names from font file paths.
 * Extracts "Roboto" from "assets/fonts/Roboto-400-latin.woff2"
 */
export const extractFontFamiliesFromPaths = (fontPaths: string[]): Set<string> => {
    const families = new Set<string>()
    for (const path of fontPaths) {
        const filename = path.split("/").pop() || ""
        const familyMatch = filename.match(/^(.+?)-\d/)
        if (familyMatch) {
            families.add(familyMatch[1].replace(/-/g, " "))
        }
    }
    return families
}
